import { createContext, useCallback, useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { rollingUtcRangeDays } from "@/utils/dateRangeUtc";

const DateRangeContext = createContext(null);

function parseUrlRange(searchParams) {
  const s = searchParams.get("start");
  const e = searchParams.get("end");
  if (!s || !e) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s) || !/^\d{4}-\d{2}-\d{2}$/.test(e)) return null;
  if (s > e) return null;
  const d0 = new Date(`${s}T00:00:00.000Z`);
  const d1 = new Date(`${e}T00:00:00.000Z`);
  const span = (d1.getTime() - d0.getTime()) / 86400000 + 1;
  if (span > 365) return null;
  return { startDate: s, endDate: e };
}

/** URL-synced UTC date range (?start=YYYY-MM-DD&end=YYYY-MM-DD). Default rolling 7 days. */
export function DateRangeProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();

  const range = useMemo(() => parseUrlRange(searchParams) || rollingUtcRangeDays(7), [searchParams]);

  const setRange = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set("start", next.startDate);
          p.set("end", next.endDate);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const value = useMemo(() => ({ startDate: range.startDate, endDate: range.endDate, setRange }), [range, setRange]);
  return <DateRangeContext.Provider value={value}>{children}</DateRangeContext.Provider>;
}

/** @returns {{ startDate: string, endDate: string, setRange: (r: { startDate: string, endDate: string }) => void }} */
// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with its provider
export function useDateRange() {
  const ctx = useContext(DateRangeContext);
  if (!ctx) throw new Error("useDateRange must be used within DateRangeProvider");
  return ctx;
}
